import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PortalLayout from "@/layouts/PortalLayout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Newspaper, Calendar as Cal, Users, Megaphone, IdCard, Search, ChevronRight } from "lucide-react";
import { useLang } from "@/context/LangContext";


const KPI = ({ icon: I, label, value, sub }) => (
  <div className="rounded-2xl border border-neutral-100 bg-white p-5">
    <div className="grid h-10 w-10 place-items-center rounded-xl bg-wine-50 text-wine-600"><I className="h-5 w-5" /></div>
    <div className="mt-3 font-serif text-3xl font-semibold text-neutral-900">{value}</div>
    <div className="text-xs font-medium text-neutral-600">{label}</div>
    {sub && <div className="mt-1 text-xs text-wine-600">{sub}</div>}
  </div>
);
export default function PressDashboard() {
  const { t } = useLang();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [news, setNews] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");

  useEffect(() => {
    api.get("/press/stats").then((r) => setStats(r.data)).catch(() => {});
    const params = cat ? { category: cat } : {};
    api.get("/press/news", { params }).then((r) => setNews(r.data)).catch(() => {});
  }, [cat]);

  const filtered = q ? news.filter((n) => n.title.toLowerCase().includes(q.toLowerCase())) : news;
  const cats = [...new Set(news.map((n) => n.category))];

  return (
    <PortalLayout sidebar="press">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-navy md:text-4xl">Bonjour {user?.first_name} 👋</h1>
          <p className="mt-1 text-neutral-600">{t("press_pressdashboard_01")}</p>
        </div>
      </div>

      <div className="rounded-2xl border border-wine-200 bg-wine-50/60 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-white text-wine-600"><IdCard className="h-5 w-5" /></div>
            <div>
              <div className="font-serif text-base font-semibold text-neutral-900">{t("press_pressdashboard_02")}</div>
              <div className="text-xs text-neutral-600">{t("press_pressdashboard_03")}</div>
            </div>
          </div>
          <button className="rounded-full border border-wine-300 bg-white px-4 py-2 text-xs font-semibold text-wine-700 hover:bg-wine-100" data-testid="press-view-profile">{t("press_pressdashboard_04")}</button>
        </div>
      </div>

      {stats && (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KPI icon={Newspaper} label="News disponibles" value={stats.news} sub="+12 cette semaine" />
          <KPI icon={Cal} label={t("press_pressdashboard_90")} value={stats.upcoming_events} sub="+5 cette semaine" />
          <KPI icon={Users} label="Membres" value={stats.members} sub="+18 ce mois-ci" />
          <KPI icon={Megaphone} label={t("press_pressdashboard_91")} value={stats.press_releases} sub="+7 ce mois-ci" />
        </div>
      )}

      <div className="mt-8 grid gap-6 lg:grid-cols-[2fr_1fr]">
        <div className="rounded-2xl border border-neutral-100 bg-white p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-serif text-xl text-neutral-900">{t("press_pressdashboard_05")}</h2>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("press_pressdashboard_92")} className="rounded-full border border-neutral-200 bg-wine-50/30 py-2 pl-9 pr-3 text-xs" data-testid="press-search" />
            </div>
          </div>
          <div className="mt-5 divide-y divide-neutral-100">
            {filtered.slice(0, 10).map((n) => (
              <Link key={n.id} to={`/news/${n.slug}`} className="flex items-center gap-4 py-4 hover:bg-wine-50/30 -mx-2 px-2 rounded-lg" data-testid={`press-news-${n.id}`}>
                {n.image && <img src={n.image} alt="" className="h-16 w-24 rounded-lg object-cover" />}
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-base text-neutral-900">{n.title}</div>
                  <div className="mt-0.5 line-clamp-1 text-xs text-neutral-500">{n.excerpt}</div>
                </div>
                <span className="hidden sm:inline rounded-full bg-wine-50 px-2.5 py-1 text-xs font-semibold text-wine-700">{n.category}</span>
                <span className="text-xs text-neutral-500">{n.published_at && new Date(n.published_at).toLocaleDateString("fr", { day: "numeric", month: "short", year: "numeric" })}</span>
                <ChevronRight className="h-4 w-4 text-neutral-400" />
              </Link>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-2xl border border-neutral-100 bg-white p-5">
            <h3 className="font-serif text-base text-neutral-900">{t("press_pressdashboard_06")}</h3>
            <div className="mt-3 space-y-1">
              <button onClick={() => setCat("")} className={`flex w-full items-center justify-between rounded-lg px-3 py-1.5 text-xs ${cat === "" ? "bg-wine-50 font-semibold text-wine-700" : "text-neutral-600 hover:bg-wine-50/50"}`} data-testid="press-cat-all">
                <span>{t("press_pressdashboard_07")}</span><span>{news.length}</span>
              </button>
              {cats.map((c) => (
                <button key={c} onClick={() => setCat(c)} className={`flex w-full items-center justify-between rounded-lg px-3 py-1.5 text-xs ${cat === c ? "bg-wine-50 font-semibold text-wine-700" : "text-neutral-600 hover:bg-wine-50/50"}`} data-testid={`press-cat-${c}`}>
                  <span>{c}</span><span>{news.filter((x) => x.category === c).length}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-wine-100 bg-wine-50/40 p-5">
            <h3 className="font-serif text-base text-neutral-900">Besoin d&apos;informations ?</h3>
            <p className="mt-1.5 text-xs text-neutral-600">{t("press_pressdashboard_08")}</p>
            <Link to="/contact?source=press-help" className="mt-3 block w-full rounded-full bg-wine-500 px-4 py-2 text-center text-xs font-semibold text-white hover:bg-wine-600" data-testid="press-contact-us">Nous contacter</Link>
          </div>
        </div>
      </div>
    </PortalLayout>
  );
}
